import { BusinessRounded, CheckCircle, Close, PersonRounded, SearchOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { styleConfig } from "../../config/index";
import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { useModals } from "../../context/ModalContext";
import { replaceNotifications, updateNotifications } from "../../redux/features/notificationSlice";
import { selectRole } from "../../redux/features/user/selectors/userState.selectors";
import { LoaderComponent } from "../Loader";
import { colors } from "../ui/colors";
import { CompanyAvatar } from "./CompanyAvatar";
import { useQueryClient } from "@tanstack/react-query";
import { AdminInvitationNotificationDialog } from "../notifications/AdminInvitationNotificationDialog";
import { useOptionalChatDock } from "../chat-dock";
import { NotificationModals } from "../notifications/NotificationModals";
import {
  ENotificationTargetKind,
  dedupeNotificationsById,
  getAccountNotificationDisplayParts,
  getNotificationChatMeta,
  getNotificationChatTarget,
  getInboundMessageSenderCompany,
  getNotificationCompany,
  getNotificationDisplayMessage,
  getNotificationTarget,
  getUniqueNotificationTargets,
  hasNotificationActionFor,
  isInboundMessageNotification,
  NOTIFICATION_ICONS,
  triggerNotificationTarget,
} from "../notifications/notificationHelpers";
import { NOTIFICATION_STATS_QUERY_KEY, useNotificationStatsQuery } from "@components/notifications/hook/useNotificationStatsQuery";
import { NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY } from "./SidebarConnect/hook/useNotificationCountByCompany";
import { getBasePath, getMyNotifications, markAsReadAllMyNotifications, updateMyNotificatioById } from "../../utils";
import { getNotificationCompanyName, useEnsureNotificationCompany } from "../notifications/useEnsureNotificationCompany";
import { useDialog } from "../ui/dialogs/useDialog";
import { getModalKeysByStackOrder, saveNotificationReturnState } from "../notifications/notificationReturnState";

const isCannotFindNotificationsError = (error) =>
  error?.code === "ERR_CANNOT_FIND_NOTIFICATIONS" ||
  error?.status === "It was not posible to find your notification." ||
  error?.status === "It was not possible to find your notification.";

export const NotificationsCenter = () => {
  const dispatch = useDispatch();
  const notificationsState = useSelector((state) => state.notificationsState);
  const { notifications } = notificationsState;
  const companies = useSelector((state) => state.userState.companies) || [];
  const userState = useSelector((state) => state.userState);
  const activeCompanyId = useSelector((state) => state.userState.activeCompanyId ?? state.userState.company?.id);
  const roleType = useSelector(selectRole);
  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openCalls } = useFloatingBubbles();
  const chatDock = useOptionalChatDock();
  const { modals } = useModals();
  const { ensureNotificationCompany, shouldSwitchNotificationCompany } = useEnsureNotificationCompany();
  const { showDialog, DialogComponent } = useDialog();
  const { showBoundary } = useErrorBoundary();
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedAdminInvitationUid, setSelectedAdminInvitationUid] = useState(null);
  const [selectedAdminNotificationId, setSelectedAdminNotificationId] = useState(null);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [companyNotifications, setCompanyNotifications] = useState([]);
  const [companyNotificationStats, setCompanyNotificationStats] = useState(null);
  const [accountCompanyFilterId, setAccountCompanyFilterId] = useState(null);
  const PAGE_SIZE = 6;
  const companyIdParam = new URLSearchParams(location.search).get("companyId");
  const selectedCompanyId = Number.isFinite(Number(companyIdParam)) && Number(companyIdParam) > 0 ? Number(companyIdParam) : null;
  const effectiveCompanyId = selectedCompanyId || accountCompanyFilterId;
  const basePath = getBasePath(roleType);
  const title = selectedCompanyId ? "Notifications company" : "Notifications account";
  const userName = [userState?.firstName, userState?.lastName].filter(Boolean).join(" ").trim();
  const accountName = userName || "your account";
  const selectedCompanyMembership = companies.find((company) => String(company.companyId) === String(selectedCompanyId));
  const companyLabel = selectedCompanyMembership?.company?.businessName || userState?.company?.businessName;
  const subtitleName = selectedCompanyId ? companyLabel || "this company" : accountName;

  const fetchCompanyNotificationStats = async () => {
    if (!effectiveCompanyId) {
      setCompanyNotificationStats(null);
      return;
    }

    try {
      const [allRes, unreadRes] = await Promise.all([
        getMyNotifications(token, 1, 1, { companyId: effectiveCompanyId }),
        getMyNotifications(token, 1, 1, { companyId: effectiveCompanyId, isRead: false }),
      ]);

      setCompanyNotificationStats({
        total: Number(allRes?.total ?? allRes?.count ?? allRes?.items?.length ?? 0),
        unread: Number(unreadRes?.total ?? unreadRes?.count ?? unreadRes?.items?.length ?? 0),
      });
    } catch (error) {
      if (isCannotFindNotificationsError(error)) {
        setCompanyNotificationStats({ total: 0, unread: 0 });
        return;
      }

      throw error;
    }
  };

  const markAsRead = async (notificationId) => {
    const currentNotifications = effectiveCompanyId ? companyNotifications : notifications;
    const notification = currentNotifications.find((item) => item.id === notificationId);

    if (!notification || notification.isRead) {
      return;
    }

    const updatedNotifications = currentNotifications.map((item) =>
      item.id === notificationId
        ? {
            ...item,
            isRead: true,
          }
        : item,
    );

    if (effectiveCompanyId) {
      setCompanyNotifications(updatedNotifications);
    } else {
      dispatch(
        updateNotifications({
          notifications: updatedNotifications,
        }),
      );
    }

    try {
      await updateMyNotificatioById(sessionStorage.getItem("token"), notificationId, {
        isRead: true,
      });

      await queryClient.invalidateQueries({
        queryKey: effectiveCompanyId ? NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY : NOTIFICATION_STATS_QUERY_KEY,
      });
    } catch (error) {
      if (effectiveCompanyId) {
        setCompanyNotifications(currentNotifications);
      } else {
        dispatch(
          updateNotifications({
            notifications,
          }),
        );
      }

      showBoundary(error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);

      await markAsReadAllMyNotifications(token, effectiveCompanyId ? { companyId: effectiveCompanyId } : undefined);

      const currentNotifications = effectiveCompanyId ? companyNotifications : notifications;
      const updatedNotifications = currentNotifications.map((notification) => ({
        ...notification,
        isRead: true,
      }));

      if (effectiveCompanyId) {
        setCompanyNotifications(updatedNotifications);
      } else {
        dispatch(
          updateNotifications({
            notifications: updatedNotifications,
          }),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: effectiveCompanyId ? NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY : NOTIFICATION_STATS_QUERY_KEY,
      });

      if (effectiveCompanyId) {
        await fetchCompanyNotificationStats();
      }

      if (filter === "unread") {
        fetchNotifications(1, "unread");
      }
    } catch (error) {
      showBoundary(error);
    } finally {
      setLoading(false);
    }
  };

  const openNotification = async (notification) => {
    const target = getNotificationTarget(notification);
    const chatTarget = getNotificationChatTarget(notification);

    if (!target && !chatTarget) {
      markAsRead(notification.id);
      return;
    }

    if (target?.kind === ENotificationTargetKind.AdminInvitation) {
      setSelectedAdminInvitationUid(String(target.id));
      setSelectedAdminNotificationId(notification.id);
      return;
    }

    try {
      await ensureNotificationCompany(notification);

      if (target && triggerNotificationTarget(target)) {
        markAsRead(notification.id);
        return;
      }

      if (chatTarget && chatDock) {
        openCalls();
        chatDock.openChat(chatTarget, getNotificationChatMeta(notification, companies));
        markAsRead(notification.id);
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (shouldSwitchNotificationCompany(notification)) {
      const companyName = getNotificationCompanyName(notification, companies) || "another company";

      showDialog({
        type: "confirm",
        title: "Switch company?",
        message: `You are about to switch to ${companyName} to open this notification. Are you sure?`,
        confirmLabel: "Yes, switch",
        cancelLabel: "Cancel",
        onCancel: () => {},
        onConfirm: () => {
          saveNotificationReturnState({
            companyId: activeCompanyId,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            modalKeys: getModalKeysByStackOrder(modals),
          });
          openNotification(notification);
        },
      });
      return;
    }

    openNotification(notification);
  };

  const fetchNotifications = (page, selectedFilter = filter, searchValue = search) => {
    const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;

    const normalizedSearch = searchValue.trim();

    const where = {
      ...(effectiveCompanyId && {
        companyId: effectiveCompanyId,
      }),
      ...(selectedFilter === "unread" && {
        isRead: false,
      }),
      ...(normalizedSearch && {
        search: normalizedSearch,
      }),
    };

    setSearching(true);

    getMyNotifications(token, safePage, PAGE_SIZE, Object.keys(where).length ? where : undefined)
      .then((res) => {
        if (!res) return;

        const incoming = res?.items ?? [];

        if (effectiveCompanyId) {
          setCompanyNotifications(incoming);
        } else {
          dispatch(
            replaceNotifications({
              notifications: incoming,
            }),
          );
        }

        setCurrentPage(Number(res.page) || safePage);
        setTotalPages(Number(res.totalPages) || 0);

        if (effectiveCompanyId) {
          fetchCompanyNotificationStats();
        }
      })
      .catch((error) => {
        if (isCannotFindNotificationsError(error)) {
          if (effectiveCompanyId) {
            setCompanyNotifications([]);
          } else {
            dispatch(replaceNotifications({ notifications: [] }));
          }

          setCurrentPage(safePage);
          setTotalPages(0);
          setCompanyNotificationStats(effectiveCompanyId ? { total: 0, unread: 0 } : null);
          return;
        }

        showBoundary(error);
      })
      .finally(() => {
        setSearching(false);
      });
  };

  const handleFilterChange = (selectedFilter) => {
    setFilter(selectedFilter);
    if (selectedFilter === "all") {
      setAccountCompanyFilterId(null);
    }
    setCurrentPage(1);
  };

  const handleCompanyFilterChange = (companyId) => {
    setAccountCompanyFilterId(companyId);
    setSearch("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  useEffect(() => {
    setCompanyNotifications([]);
    setCompanyNotificationStats(null);
    setCurrentPage(1);
  }, [effectiveCompanyId]);

  useEffect(() => {
    const normalizedSearch = search.trim();

    if (normalizedSearch.length === 1) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setAppliedSearch(normalizedSearch);
      setCurrentPage(1);
      fetchNotifications(1, filter, normalizedSearch);
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [filter, search, effectiveCompanyId]);

  const visibleNotifications = useMemo(() => {
    const normalizedSearch = appliedSearch.trim().toLowerCase();

    const sourceNotifications = dedupeNotificationsById(effectiveCompanyId ? companyNotifications : notifications);

    return sourceNotifications.filter((notification) => {
      if (filter === "unread" && notification.isRead) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const displayMessage = getNotificationDisplayMessage(notification, companies);

      const shouldSearchMeta = /^\d+$/.test(normalizedSearch) || /^[a-z]+-\d+$/i.test(normalizedSearch);

      const searchableContent = [displayMessage, notification.type, ...(shouldSearchMeta ? [JSON.stringify(notification.meta ?? {})] : [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableContent.includes(normalizedSearch);
    });
  }, [notifications, companyNotifications, effectiveCompanyId, filter, appliedSearch, companies]);

  const uniqueTargets = useMemo(() => getUniqueNotificationTargets(visibleNotifications), [visibleNotifications]);

  const { data: notificationStats } = useNotificationStatsQuery();

  const totalNotifications = Number(notificationStats?.total ?? 0);
  const unreadNotificationsCount = Number(notificationStats?.unread ?? 0);
  const displayTotalNotifications = effectiveCompanyId ? Number(companyNotificationStats?.total ?? 0) : totalNotifications;
  const displayUnreadNotificationsCount = effectiveCompanyId ? Number(companyNotificationStats?.unread ?? 0) : unreadNotificationsCount;
  const notificationsListRef = useRef(null);
  return (
    <>
      <NotificationModals targets={uniqueTargets} keyPrefix="NotificationsCenter" />

      <AdminInvitationNotificationDialog
        invitationUid={selectedAdminInvitationUid}
        notificationId={selectedAdminNotificationId}
        onClose={() => {
          setSelectedAdminInvitationUid(null);
          setSelectedAdminNotificationId(null);
        }}
        onMarkAsRead={markAsRead}
      />

      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "80%",
          minHeight: "100%",
          margin: "auto",
          flexDirection: "column",
        }}
      >
        <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
          <Button
            onClick={markAllAsRead}
            disabled={displayUnreadNotificationsCount === 0 || loading}
            sx={{
              position: "absolute",
              right: 44,
              top: -8,
              height: 32,
              color: colors.orange.base,
              borderColor: colors.orange.base,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: colors.orange.base,
                backgroundColor: "rgba(252, 151, 0, 0.08)",
              },
            }}
            variant="outlined"
          >
            Read all
          </Button>
          <IconButton
            onClick={() => navigate(basePath, { replace: true })}
            sx={{
              position: "absolute",
              right: 0,
              top: -8,
              width: 32,
              height: 32,
              color: "#6f8798",
              backgroundColor: "var(--fx-colors-info-100, #DBEAFE)",
              "&:hover": {
                backgroundColor: "var(--fx-colors-info-100, #DBEAFE)",
              },
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedCompanyId ? (
                <BusinessRounded sx={{ color: colors.orange.base, fontSize: 22 }} />
              ) : (
                <PersonRounded sx={{ color: colors.orange.base, fontSize: 22 }} />
              )}

              <Typography variant="h5" sx={{ color: colors.orange.base, fontWeight: 500 }}>
                {title}
              </Typography>
            </Box>

            <Typography sx={{ mt: 0.4, fontSize: 12, color: "text.secondary", fontWeight: 500 }}>
              Viewing notifications for{" "}
              <Box component="span" sx={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 700 }}>
                {subtitleName}
              </Box>
            </Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          size="small"
          value={search}
          placeholder="Search notifications"
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              endAdornment: searching ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{
            mb: 2,
            maxWidth: 500,
            alignSelf: "flex-start",
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "background.paper",
              "&.Mui-focused fieldset": {
                borderColor: colors.orange.base,
              },
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            mb: 2,
            overflowX: "auto",
            pb: 0.5,
          }}
        >
          {[
            {
              value: "all",
              label: "All",
              count: displayTotalNotifications,
            },
            {
              value: "unread",
              label: "Unread",
              count: displayUnreadNotificationsCount,
            },
          ].map((item) => {
            const isSelected = filter === item.value;

            return (
              <Button
                key={item.value}
                variant="outlined"
                onClick={() => handleFilterChange(item.value)}
                sx={{
                  height: 32,
                  minWidth: "auto",
                  px: 1.5,
                  borderRadius: "8px",
                  borderColor: colors.orange.base,
                  color: isSelected ? "white" : colors.orange.base,
                  backgroundColor: isSelected ? colors.orange.base : "transparent",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: colors.orange.base,
                    color: isSelected ? "white" : colors.orange.base,
                    backgroundColor: isSelected ? colors.orange.base : "rgba(252, 151, 0, 0.08)",
                  },
                }}
              >
                {item.label} ({item.count})
              </Button>
            );
          })}

          {!selectedCompanyId && companies.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.12)" }} />

              {companies.map((membership) => {
                const isSelected = String(accountCompanyFilterId) === String(membership.companyId);
                const companyName = membership.company?.businessName || "Company";

                return (
                  <Tooltip
                    key={`notifications-company-filter-${membership.companyId}`}
                    title={`Select to view only ${companyName}`}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleCompanyFilterChange(membership.companyId)}
                      sx={{
                        height: 32,
                        minWidth: "auto",
                        maxWidth: 190,
                        px: 1,
                        borderRadius: "8px",
                        borderColor: colors.orange.base,
                        color: isSelected ? "white" : colors.orange.base,
                        backgroundColor: isSelected ? colors.orange.base : "transparent",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                        gap: 0.75,
                        "&:hover": {
                          borderColor: colors.orange.base,
                          color: isSelected ? "white" : colors.orange.base,
                          backgroundColor: isSelected ? colors.orange.base : "rgba(252, 151, 0, 0.08)",
                        },
                      }}
                    >
                      <CompanyAvatar company={membership.company} size={18} />
                      <Typography noWrap sx={{ fontSize: "0.8rem", fontWeight: 700, maxWidth: 135 }}>
                        {companyName}
                      </Typography>
                    </Button>
                  </Tooltip>
                );
              })}
            </>
          )}
        </Box>

        <List
          ref={notificationsListRef}
          sx={{
            backgroundColor: "background.paper",
            width: "100%",
            height: 400,
            minHeight: 400,
            maxHeight: 400,
            borderRadius: "30px",
            margin: 0,
            padding: 0,
            border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.075)",
            overflowX: "hidden",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {searching ? (
            <Fade in timeout={250}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: 398,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CircularProgress
                  size={30}
                  sx={{
                    color: colors.orange.base,
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Searching notifications...
                </Typography>
              </Box>
            </Fade>
          ) : visibleNotifications.length > 0 ? (
            <Fade in timeout={300}>
              <Box
                sx={{
                  width: "100%",
                }}
              >
                {visibleNotifications.map((notification, index) => {
                  const displayParts = getAccountNotificationDisplayParts(notification, companies);

                  return (
                    <Box
                    key={`notification-item-${notification.id}`}
                    sx={{
                      width: "100%",
                      animation: "notificationFadeIn 280ms ease both",
                      animationDelay: `${Math.min(index * 25, 200)}ms`,

                      "@keyframes notificationFadeIn": {
                        from: {
                          opacity: 0,
                          transform: "translateY(5px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateY(0)",
                        },
                      },
                    }}
                  >
                    <ListItem
                      sx={{
                        opacity: notification.isRead ? 0.5 : 1,
                        transition: "opacity 180ms ease",
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          cursor: hasNotificationActionFor(notification) ? "pointer" : "default",
                          borderRadius: "10px",
                          transition: "background-color 180ms ease, transform 180ms ease",

                          "&:hover": {
                            backgroundColor: "rgba(252, 151, 0, 0.06)",
                          },
                        }}
                      >
                        <ListItemIcon>
                          {getNotificationCompany(notification, companies) || isInboundMessageNotification(notification) ? (
                            <CompanyAvatar
                              company={getNotificationCompany(notification, companies) || getInboundMessageSenderCompany(notification, companies)}
                              size={28}
                            />
                          ) : (
                            (NOTIFICATION_ICONS[notification?.type] ?? <CheckCircle htmlColor="#fc9700" />)
                          )}
                        </ListItemIcon>

                        <ListItemText
                          primary={
                            selectedCompanyId
                              ? getNotificationDisplayMessage(notification, companies)
                              : (
                                <>
                                  {displayParts.prefix && <Box component="span" sx={{ fontWeight: 900 }}>{displayParts.prefix}</Box>}
                                  {displayParts.message}
                                </>
                              )
                          }
                          sx={{
                            whiteSpace: "normal",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: styleConfig.sidebar.bg,
                          }}
                        />

                        {!notification.isRead ? (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: colors.orange.base,
                              flex: "0 0 auto",
                              ml: 2,
                            }}
                          />
                        ) : null}
                      </ListItemButton>
                    </ListItem>

                    <Divider
                      sx={{
                        width: "95%",
                        mx: "auto",
                      }}
                    />
                    </Box>
                  );
                })}
              </Box>
            </Fade>
          ) : (
            <Fade in timeout={300}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: 398,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  px: 3,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(252, 151, 0, 0.1)",
                    mb: 2,
                  }}
                >
                  <SearchOff
                    sx={{
                      fontSize: 36,
                      color: colors.orange.base,
                    }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: styleConfig.sidebar.bg,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {appliedSearch ? "No notifications found" : filter === "unread" ? "No unread notifications" : "No notifications yet"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 360,
                  }}
                >
                  {appliedSearch
                    ? `We couldn't find any notifications matching "${appliedSearch}".`
                    : filter === "unread"
                      ? "You're all caught up. There are no unread notifications."
                      : "Your notifications will appear here when you receive them."}
                </Typography>

                {appliedSearch ? (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearch("");
                      setAppliedSearch("");
                    }}
                    sx={{
                      mt: 2,
                      height: 32,
                      borderColor: colors.orange.base,
                      color: colors.orange.base,
                      textTransform: "none",
                      fontWeight: 600,

                      "&:hover": {
                        borderColor: colors.orange.base,
                        backgroundColor: "rgba(252, 151, 0, 0.08)",
                      },
                    }}
                  >
                    Clear search
                  </Button>
                ) : null}
              </Box>
            </Fade>
          )}
        </List>

        {totalPages > 1 ? (
          <Pagination
            page={currentPage}
            count={totalPages}
            shape="rounded"
            onChange={(_, page) => {
              if (page === currentPage || searching) {
                return;
              }

              fetchNotifications(page, filter, appliedSearch);
            }}
            disabled={searching}
            sx={{
              mt: 2,
              "& .MuiPaginationItem-root": {
                color: styleConfig.sidebar.bg,
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                color: "white",
                backgroundColor: colors.orange.base,
                "&:hover": {
                  backgroundColor: colors.orange.base,
                },
              },
            }}
          />
        ) : null}
      </Box>
      <LoaderComponent open={loading} type="spinner" />
      {DialogComponent}
    </>
  );
};
